import * as React from "react";
import InputAdornment from "@mui/material/InputAdornment";
import OutlinedInput, { type OutlinedInputProps } from "@mui/material/OutlinedInput";

import { cn } from "../../lib/utils";

type NativeInputPassthroughProps = Pick<
  React.InputHTMLAttributes<HTMLInputElement>,
  "accept" | "inputMode" | "max" | "maxLength" | "min" | "minLength" | "multiple" | "pattern" | "step"
>;

type InputIconProps = {
  /** Icon displayed before the input value. */
  startIcon?: React.ReactNode;
  /** Icon displayed after the input value. */
  endIcon?: React.ReactNode;
  /** Makes the start icon interactive when provided. */
  onStartIconClick?: React.MouseEventHandler<HTMLButtonElement>;
  /** Makes the end icon interactive when provided. */
  onEndIconClick?: React.MouseEventHandler<HTMLButtonElement>;
  /** Accessible label required by interactive start icons. */
  startIconAriaLabel?: string;
  /** Accessible label required by interactive end icons. */
  endIconAriaLabel?: string;
};

export type InputProps = OutlinedInputProps & NativeInputPassthroughProps & InputIconProps;

type IconAdornmentProps = {
  position: "start" | "end";
  icon: React.ReactNode;
  onClick?: React.MouseEventHandler<HTMLButtonElement>;
  ariaLabel?: string;
  disabled?: boolean;
};

const IconAdornment = ({
  position,
  icon,
  onClick,
  ariaLabel,
  disabled,
}: IconAdornmentProps) => (
  <InputAdornment position={position} disablePointerEvents={!onClick}>
    {onClick ? (
      <button
        type="button"
        onClick={onClick}
        disabled={disabled}
        aria-label={ariaLabel}
        className="inline-flex items-center justify-center rounded-sm border-0 bg-transparent p-0 text-inherit transition-colors hover:text-gray-600 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#00c49a] disabled:cursor-not-allowed"
      >
        {icon}
      </button>
    ) : (
      <span aria-hidden="true" className="inline-flex items-center justify-center">
        {icon}
      </span>
    )}
  </InputAdornment>
);

const Input = React.forwardRef<
  HTMLInputElement,
  InputProps
>(({
  className,
  type,
  onBlur,
  onInput,
  accept,
  inputMode,
  max,
  maxLength,
  min,
  minLength,
  multiple,
  pattern,
  step,
  inputProps,
  startIcon,
  endIcon,
  onStartIconClick,
  onEndIconClick,
  startIconAriaLabel,
  endIconAriaLabel,
  startAdornment,
  endAdornment,
  disabled,
  ...props
}, ref) => {
  const handleInput = (e: React.FormEvent<HTMLInputElement>) => {
    if (onInput) {
      onInput(e);
    }

    const target = e.currentTarget;
    if (target) {
      target.scrollIntoView({
        behavior: "smooth",
        block: "nearest",
        inline: "nearest",
      });
    }
  };

  return (
    <OutlinedInput
      fullWidth
      type={type}
      inputRef={ref}
      onBlur={(event) => {
        // MUI may call this without an event while disabling a focused input.
        if (event) {
          onBlur?.(event);
        }
      }}
      onInput={handleInput}
      disabled={disabled}
      className={cn("rounded-md", className)}
      startAdornment={
        startAdornment ??
        (startIcon ? (
          <IconAdornment
            position="start"
            icon={startIcon}
            onClick={onStartIconClick}
            ariaLabel={startIconAriaLabel}
            disabled={disabled}
          />
        ) : undefined)
      }
      endAdornment={
        endAdornment ??
        (endIcon ? (
          <IconAdornment
            position="end"
            icon={endIcon}
            onClick={onEndIconClick}
            ariaLabel={endIconAriaLabel}
            disabled={disabled}
          />
        ) : undefined)
      }
      sx={{
        minHeight: 40,
        borderRadius: "0.375rem",
        bgcolor: "background.paper",
        color: "text.primary",
        "& .MuiOutlinedInput-notchedOutline": {
          borderColor: "#DCDCDC",
        },
        "&:hover .MuiOutlinedInput-notchedOutline": {
          borderColor: "#00C49A",
        },
        "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
          borderColor: "#00C49A",
          borderWidth: 2,
        },
        "&:active .MuiOutlinedInput-notchedOutline": {
          borderColor: "#00C49A",
        },
        "&.Mui-disabled": {
          opacity: 0.5,
        },
        "& input": {
          padding: "10px 12px",
          fontSize: "0.875rem",
          lineHeight: 1.25,
        },
        "& input::placeholder": {
          color: "rgba(0, 0, 0, 0.38)",
          opacity: 1,
        },
        "& .MuiInputAdornment-root": {
          color: "text.secondary",
        },
        "@media (prefers-color-scheme: dark)": {
          bgcolor: "#1f2937",
          color: "#f9fafb",
          "& .MuiOutlinedInput-notchedOutline": {
            borderColor: "rgba(255, 255, 255, 0.23)",
          },
          "&:hover .MuiOutlinedInput-notchedOutline": {
            borderColor: "#00C49A",
          },
          "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
            borderColor: "#00C49A",
            borderWidth: 2,
          },
          "&:active .MuiOutlinedInput-notchedOutline": {
            borderColor: "#00C49A",
          },
          "& input::placeholder": {
            color: "rgba(255, 255, 255, 0.5)",
          },
        },
      }}
      inputProps={{
        ...inputProps,
        accept: accept ?? inputProps?.accept,
        inputMode: inputMode ?? inputProps?.inputMode,
        max: max ?? inputProps?.max,
        maxLength: maxLength ?? inputProps?.maxLength,
        min: min ?? inputProps?.min,
        minLength: minLength ?? inputProps?.minLength,
        multiple: multiple ?? inputProps?.multiple,
        pattern: pattern ?? inputProps?.pattern,
        step: step ?? inputProps?.step,
      }}
      {...props}
    />
  );
});
Input.displayName = "Input";

export { Input };
